import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectByIdQuery, useProjectsQuery } from '@/features/projects/hooks/projects';
import {
  shouldGroupErroredWithFailed,
  shouldPreferFileNameOverDisplayName,
} from '@/features/shared/utils/projectStatusPresentation';
import { queryKeys } from '@/shared/hooks';
import type { ProjectDto, QueryResult } from '@flowqa/shared';

const resolveCachedProject = ({
  orgId,
  projectId,
  queryClient,
}: {
  orgId: string | null;
  projectId: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
}): ProjectDto | null => {
  if (!projectId) {
    return null;
  }

  const projectById =
    queryClient.getQueryData<ProjectDto | null>(queryKeys.projectById(projectId, orgId)) ?? null;
  if (projectById) {
    return projectById;
  }

  const scopedProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(
    queryKeys.projects(orgId),
  );
  return scopedProjects?.items.find((project) => project.id === projectId) ?? null;
};

export const useProjectStatusPresentation = (): {
  groupErroredWithFailed: boolean;
  preferFileNameOverDisplayName: boolean;
  statusPresentationLoading: boolean;
  resolveGroupErroredWithFailed: (projectId?: string | null) => boolean;
  resolvePreferFileNameOverDisplayName: (projectId?: string | null) => boolean;
} => {
  const { selectedProjectId } = useProjectFilter();
  const { selectedOrganisationId } = useOrganisationFilter();
  const queryClient = useQueryClient();
  const projectQuery = useProjectByIdQuery(selectedProjectId, {
    refetchOnWindowFocus: false,
    staleTimeMs: 60_000,
  });
  const projectsQuery = useProjectsQuery({
    enabled: !selectedProjectId,
    refetchOnWindowFocus: false,
    staleTimeMs: 60_000,
  });
  const cachedProject = resolveCachedProject({
    orgId: selectedOrganisationId ?? null,
    projectId: selectedProjectId,
    queryClient,
  });
  const effectiveProject = projectQuery.data ?? cachedProject;
  const projectSettingsById = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project]));
  }, [projectsQuery.data?.items]);
  const statusPresentationLoading =
    (selectedProjectId !== null && effectiveProject === null && projectQuery.isLoading) ||
    (selectedProjectId === null && projectsQuery.isLoading);
  const resolveProject = (projectId?: string | null): ProjectDto | null => {
    if (projectId && effectiveProject?.id === projectId) {
      return effectiveProject;
    }
    if (projectId) {
      return projectSettingsById.get(projectId) ?? null;
    }
    return effectiveProject;
  };
  const resolveGroupErroredWithFailed = (projectId?: string | null): boolean =>
    shouldGroupErroredWithFailed(resolveProject(projectId), projectId ?? selectedProjectId ?? null);
  const resolvePreferFileNameOverDisplayName = (projectId?: string | null): boolean =>
    shouldPreferFileNameOverDisplayName(
      resolveProject(projectId),
      projectId ?? selectedProjectId ?? null,
    );

  return {
    groupErroredWithFailed: resolveGroupErroredWithFailed(selectedProjectId),
    preferFileNameOverDisplayName: resolvePreferFileNameOverDisplayName(selectedProjectId),
    statusPresentationLoading,
    resolveGroupErroredWithFailed,
    resolvePreferFileNameOverDisplayName,
  };
};
